package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 게시판 신고(Play User Generated Content 정책).
 *
 * 정책은 "학교나 회사 안에서만 쓰이는 앱"을 **면제 대상이 아니라** 가장 가벼운 의무가
 * 붙는 분류로 명시한다 — 그 의무가 *"in-app functionality to report content **and
 * users**"*이다. 그래서 신고 대상은 게시글·댓글만이 아니라 사람도 포함한다
 * ([ContentReportTargetType.USER]).
 *
 * **차단(block)은 범위에 없다.** 같은 정책이 차단을 1:1 상호작용(DM·태그·멘션)이 있거나
 * 공개 UGC인 앱으로 한정하는데, 이 게시판은 학원 ACTIVE 멤버만 읽는 단일 피드이고
 * DM도 멘션도 없다. 게시판에 DM·멘션·학원 간 공용 피드 중 하나라도 생기면 이 결론은
 * 즉시 무효다 — 판단 근거는 `functions/src/reports.ts` 머리말에 원문과 함께 있다.
 *
 * 서버 계약은 `functions/src/reports.ts`: `edubridge-reportPost`,
 * `edubridge-reportComment`, `edubridge-reportUser`(ACTIVE 멤버),
 * `edubridge-resolveContentReport`(EDITOR 이상). 접수 대기열 **조회는 콜러블이 아니라**
 * Firestore 직접 읽기다 — `edubridge_academies/{academyId}/contentReports`를
 * `where('status','==','OPEN')`으로 거르며, `firestore.rules`가 스태프에게만 연다.
 */
@Serializable
enum class ContentReportTargetType { POST, COMMENT, USER }

/**
 * 신고 사유. 닫힌 집합이고 의도적으로 작다 — 항목 하나하나가 신고자가 신고 전에 내려야
 * 하는 판단이고 스태프가 읽어야 하는 열이다. [PRIVACY]는 학원 앱에서 특히 값을 한다:
 * 게시판에 올라온 전화번호·주소가 이 제품이 실제로 받게 될 신고다.
 */
@Serializable
enum class ContentReportReason(val displayName: String) {
    SPAM("스팸·광고"),
    ABUSE("욕설·괴롭힘"),
    SEXUAL("선정적 내용"),
    PRIVACY("개인정보 노출"),
    ILLEGAL("불법 정보"),
    OTHER("기타"),
}

@Serializable
enum class ContentReportStatus { OPEN, RESOLVED }

/** 처리 결과. "삭제했다"와 "신고가 타당하지 않았다"는 서로 다른 결말이다. */
@Serializable
enum class ContentReportResolution { ACTIONED, DISMISSED }

/**
 * 신고 접수 요청.
 *
 * [postId]는 [ContentReportTargetType.COMMENT]일 때만 쓰인다(댓글이 달린 글). 초대
 * (`CreateInvitationRequest`)가 `inviteeType` + `targetId`로 대상을 표현하는 것과 같은
 * 형태이며, sealed class를 쓰지 않는 이유도 같다 — Swift·JS 경계로 깨끗하게 나가지 않는다.
 */
@Serializable
data class ContentReportRequest(
    val academyId: String,
    val targetType: ContentReportTargetType,
    val targetId: String,
    val postId: String? = null,
    val reason: ContentReportReason,
    val detail: String? = null,
) {
    companion object {
        /** 서버가 거부하는 길이. 입력창 maxlength를 여기에 맞춘다. */
        const val DETAIL_MAX_LENGTH: Int = 500
    }
}

/**
 * 접수 결과.
 *
 * [alreadyReported]가 참이면 같은 사람이 같은 대상을 이미 신고해 둔 상태이고 새 행은
 * 생기지 않았다 — 신고 문서 id가 `{대상종류}_{대상id}_{신고자uid}`로 결정적이라 반복
 * 신고가 대기열을 부풀리지 못한다. 화면은 이 경우에도 실패가 아니라 "이미 접수됨"으로
 * 보여야 한다.
 */
@Serializable
data class ContentReportResult(
    val reportId: String? = null,
    val status: ContentReportStatus = ContentReportStatus.OPEN,
    val alreadyReported: Boolean = false,

    /** 스태프가 종료했던 신고를 다시 열었다. */
    val reopened: Boolean = false,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

/**
 * 스태프 대기열의 한 행. Firestore에서 직접 읽는다(위 머리말 참조).
 *
 * [reporterUid]가 들어 있어서 일반 멤버에게는 컬렉션을 열지 않는다 — 누가 자신을
 * 신고했는지 알 수 있으면 신고 자체가 위험해진다.
 */
@Serializable
data class ContentReport(
    val id: String,
    val academyId: String,
    val targetType: ContentReportTargetType,
    val targetId: String,

    /** 댓글 신고일 때 그 댓글이 달린 글. */
    val postId: String? = null,

    /** 신고된 글·댓글의 작성자, 또는 신고된 사람. */
    val targetAuthorUid: String? = null,
    val reason: ContentReportReason,
    val detail: String? = null,
    val reporterUid: String,
    val status: ContentReportStatus,
    val resolution: ContentReportResolution? = null,
    val resolutionNote: String? = null,
    val resolvedAt: String? = null,
    val resolvedBy: String? = null,

    /** 같은 신고자가 이 대상을 몇 번 올렸는지. 2 이상이면 종료가 문제를 못 끝냈다는 뜻이다. */
    val reportCount: Int = 1,
    val createdAt: String,
) {
    val isOpen: Boolean get() = status == ContentReportStatus.OPEN
}
